import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Badge, Empty } from "../components/UI";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea, SelectWithCustom } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { usePagination, PaginationBar } from "../components/Pagination";
import { Plus, Search, Play, Square, Eye, Check, X, ChevronDown, Repeat, ListPlus, Filter, Trash2, Send, MessageSquare, History, BellRing, Zap } from "lucide-react";
import { TaskFocusMode } from "../components/TaskFocusMode";
import { InfoTip } from "../components/InfoTip";
import { ExportCsvBtn } from "../components/ExportCsvBtn";
import { toast } from "sonner";

const VIEWS = [
  ["overdue", "My Overdue"], ["assigned", "My Assigned"], ["owned", "My Owned"], ["following", "Following"],
  ["recent", "Recent"], ["recurring", "Recurring"], ["queue", "Request Queue"], ["import", "Import Tasks"],
];
const STATUS_LABELS = { open: "Open", in_progress: "In Progress", manager_review: "Manager Review", submitted: "Submitted to Carelon", request_queue: "Request Queue", completed: "Completed", archived: "Archived" };
const PRIORITY_COLORS = { low: "bg-[#FDD835]", medium: "bg-[#FB8C00]", high: "bg-[#E53935]", critical: "bg-[#B71C1C]" };
const PROJECTS = ["Client Care", "Intake Pipeline", "Chart Compliance", "Utilization Review", "Revenue Cycle", "Clinical Ops", "Front Office", "Accreditation"];
const MODULES = ["Clinical Documentation", "Intake", "Billing", "Scheduling", "Compliance", "General"];
const MILESTONES = ["Kickoff", "Mid-cycle", "Final review"];
const EMPTY = { title: "", client_id: "", status: "open", project: "", priority: "medium", module: "", owned_by_id: "", start_date: "", due_date: "", estimated_hours: "", assigned_to_id: "", milestone: "", followed_by_ids: [], summary: "", send_email: false, recurrence: { enabled: false, frequency: "weekly" } };

const fmtHMS = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const liveSeconds = (t, nowTs) => (t.actual_seconds || 0) + (t.timer_started_at ? Math.max(0, Math.floor((nowTs - new Date(t.timer_started_at).getTime()) / 1000)) : 0);

function DueCell({ t }) {
  if (!t.due_date) return <span className="text-[#84675B]">-</span>;
  const days = dayjs().startOf("day").diff(dayjs(t.due_date), "day");
  const overdue = days > 0 && !["completed", "archived"].includes(t.status);
  return (
    <span className={overdue ? "text-[#C62828] font-semibold" : "text-[#4E342E]"}>
      {dayjs(t.due_date).format("MM/DD/YYYY")}{overdue && <span className="text-xs"> ({days} day{days === 1 ? "" : "s"} Overdue)</span>}
    </span>
  );
}

function StatusChip({ status, meta }) {
  const m = meta?.[status];
  if (!m) return <Badge value={status} />;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap" style={{ backgroundColor: `${m.color}1F`, color: m.color }} data-testid={`status-chip-${status}`}>
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: m.color }} />{m.label}
    </span>
  );
}

function TaskForm({ form, setForm, users, clients, onSave, onCancel, busy, isEdit, statusDefs }) {
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const pickable = (statusDefs || []).filter((s) => !s.archived && s.key !== "archived").map((s) => s.key);
  const statusOpts = pickable.length ? pickable : Object.keys(STATUS_LABELS);
  const statusLabel = (k) => (statusDefs || []).find((s) => s.key === k)?.label || STATUS_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const userOpts = users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>);
  const toggleFollower = (id) => setForm((s) => ({ ...s, followed_by_ids: s.followed_by_ids.includes(id) ? s.followed_by_ids.filter((x) => x !== id) : [...s.followed_by_ids, id] }));
  return (
    <form onSubmit={onSave} className="space-y-4">
      <div><FLabel>Title *</FLabel><FInput data-testid="task-title-input" value={form.title} onChange={f("title")} required /></div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
        <div><FLabel>Client</FLabel>
          <FSelect data-testid="task-client-select" value={form.client_id} onChange={f("client_id")}>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </FSelect></div>
        <div><FLabel>Status</FLabel>
          <FSelect data-testid="task-status-select" value={form.status} onChange={f("status")}>
            {[...new Set([...statusOpts, form.status].filter((k) => k && k !== "archived"))].map((k) => <option key={k} value={k}>{statusLabel(k)}</option>)}
          </FSelect></div>
        <div><FLabel>Project</FLabel><SelectWithCustom testId="task-project-select" value={form.project} onChange={(v) => setForm((s) => ({ ...s, project: v }))} options={PROJECTS} placeholder="Select here" /></div>
        <div><FLabel>Priority</FLabel>
          <FSelect data-testid="task-priority-select" value={form.priority} onChange={f("priority")}>
            {["low", "medium", "high", "critical"].map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
          </FSelect></div>
        <div><FLabel>Module</FLabel><FSelect data-testid="task-module-select" value={form.module} onChange={f("module")}>{MODULES.map((m) => <option key={m} value={m}>{m}</option>)}</FSelect></div>
        <div><FLabel>Owned by</FLabel><FSelect data-testid="task-owner-select" value={form.owned_by_id} onChange={f("owned_by_id")}>{userOpts}</FSelect></div>
        <div><FLabel>Start date</FLabel><DateField testId="task-start-date" value={form.start_date} onChange={(v) => setForm((s) => ({ ...s, start_date: v }))} /></div>
        <div><FLabel>Due date</FLabel><DateField testId="task-due-date" value={form.due_date} disabledBefore={form.start_date || undefined} onChange={(v) => setForm((s) => ({ ...s, due_date: v }))} /></div>
        <div><FLabel>Estimated (hours)</FLabel><FInput data-testid="task-estimated-input" type="number" min="0" step="0.5" value={form.estimated_hours} onChange={f("estimated_hours")} /></div>
        <div><FLabel>Assigned to</FLabel><FSelect data-testid="task-assignee-select" value={form.assigned_to_id} onChange={f("assigned_to_id")}>{userOpts}</FSelect></div>
        <div><FLabel>Milestone</FLabel><SelectWithCustom testId="task-milestone-select" value={form.milestone} onChange={(v) => setForm((s) => ({ ...s, milestone: v }))} options={MILESTONES} placeholder="Select here" /></div>
      </div>
      <div>
        <FLabel>Followed by</FLabel>
        <div className="flex flex-wrap gap-2 mt-1" data-testid="task-followers">
          {users.map((u) => {
            const on = form.followed_by_ids.includes(u.id);
            return (
              <button key={u.id} type="button" data-testid={`task-follower-${u.id}`} onClick={() => toggleFollower(u.id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#4E342E] bg-[#4E342E] text-white" : "border-[#E5DFD3] bg-white text-[#6D4C41] hover:border-[#8D6E63]"}`}>
                {u.first_name} {u.last_name}
              </button>
            );
          })}
        </div>
      </div>
      <div><FLabel>Summary</FLabel><FTextarea data-testid="task-summary-input" rows={4} value={form.summary} onChange={f("summary")} placeholder="Describe the work, links, expectations…" /></div>
      <div className="flex flex-wrap items-center gap-5">
        <label className="flex items-center gap-2 text-sm text-[#4E342E] cursor-pointer">
          <input type="checkbox" data-testid="task-send-email" className="h-4 w-4 accent-[#4E342E]" checked={form.send_email} onChange={(e) => setForm((s) => ({ ...s, send_email: e.target.checked }))} />
          Send Email notification
        </label>
        <label className="flex items-center gap-2 text-sm text-[#4E342E] cursor-pointer">
          <input type="checkbox" data-testid="task-recurrence" className="h-4 w-4 accent-[#4E342E]" checked={form.recurrence.enabled} onChange={(e) => setForm((s) => ({ ...s, recurrence: { ...s.recurrence, enabled: e.target.checked } }))} />
          Recurrence
        </label>
        {form.recurrence.enabled && (
          <FSelect data-testid="task-recurrence-freq" className="w-36 h-10" value={form.recurrence.frequency} onChange={(e) => setForm((s) => ({ ...s, recurrence: { ...s.recurrence, frequency: e.target.value } }))}>
            {["daily", "weekly", "biweekly", "monthly", "quarterly"].map((fq) => <option key={fq} value={fq}>{fq[0].toUpperCase() + fq.slice(1)}</option>)}
          </FSelect>
        )}
      </div>
      <div className="sticky bottom-0 -mx-6 -mb-5 mt-4 flex justify-end gap-2 border-t border-[#F1EEE7] bg-white px-6 py-4">
        <Btn type="button" variant="ghost" onClick={onCancel}>Back</Btn>
        <Btn data-testid="task-save-btn" type="submit" disabled={busy || !form.title.trim()}>{busy ? "Saving…" : isEdit ? "Save Changes" : "Save Task"}</Btn>
      </div>
    </form>
  );
}

function ImportTasks({ users, reload }) {
  const [rows, setRows] = useState([{ title: "", assigned_to_id: "", due_date: "", priority: "medium" }]);
  const [busy, setBusy] = useState(false);
  const set = (i, k, v) => setRows((rs) => rs.map((r, x) => (x === i ? { ...r, [k]: v } : r)));
  const save = async () => {
    setBusy(true);
    try {
      const tasks = rows.filter((r) => r.title.trim()).map((r) => ({ ...r, assigned_to_name: (users.find((u) => u.id === r.assigned_to_id) || {}).first_name ? `${users.find((u) => u.id === r.assigned_to_id).first_name} ${users.find((u) => u.id === r.assigned_to_id).last_name}` : "" }));
      const r = await api.post("/tasks/bulk", { tasks });
      toast.success(`${r.data.created} tasks created`);
      setRows([{ title: "", assigned_to_id: "", due_date: "", priority: "medium" }]);
      reload();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <Card className="p-6" data-testid="import-tasks-panel">
      <h3 className="font-heading font-bold text-[#3E2723]">Quick-add multiple tasks</h3>
      <p className="text-sm text-[#7B6157] mt-1 mb-4">Add one row per task - empty rows are skipped.</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_200px_180px_140px_40px] gap-2 items-center">
            <FInput data-testid={`bulk-title-${i}`} placeholder={`Task ${i + 1} title`} value={r.title} onChange={(e) => set(i, "title", e.target.value)} />
            <FSelect data-testid={`bulk-assignee-${i}`} value={r.assigned_to_id} onChange={(e) => set(i, "assigned_to_id", e.target.value)}>
              {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
            </FSelect>
            <DateField testId={`bulk-due-${i}`} placeholder="Due date" value={r.due_date} onChange={(v) => set(i, "due_date", v)} />
            <FSelect value={r.priority} onChange={(e) => set(i, "priority", e.target.value)}>
              {["low", "medium", "high", "critical"].map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
            </FSelect>
            <button type="button" aria-label="Remove row" onClick={() => setRows((rs) => rs.filter((_, x) => x !== i))}
              className="h-9 w-9 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <Btn variant="outline" data-testid="bulk-add-row-btn" className="rounded-lg" onClick={() => setRows((rs) => [...rs, { title: "", assigned_to_id: "", due_date: "", priority: "medium" }])}><Plus size={14} /> Add Row</Btn>
        <Btn data-testid="bulk-save-btn" onClick={save} disabled={busy || !rows.some((r) => r.title.trim())}>{busy ? "Creating…" : "Create Tasks"}</Btn>
      </div>
    </Card>
  );
}

export default function Tasks() {
  const { user } = useAuth();
  const [view, setView] = useState("overdue");
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [panel, setPanel] = useState(null); // {mode: 'new'|'edit', task?}
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const [comment, setComment] = useState("");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  const [activity, setActivity] = useState(null);
  const [panelTab, setPanelTab] = useState("details");
  const [statusDefs, setStatusDefs] = useState([]);
  const [focusOpen, setFocusOpen] = useState(false);
  const addRef = useRef(null);
  const statusMeta = useMemo(() => Object.fromEntries(statusDefs.map((s) => [s.key, s])), [statusDefs]);
  const doneKeys = useMemo(() => {
    const k = statusDefs.filter((s) => s.behavior === "done").map((s) => s.key);
    return k.length ? k : ["completed", "archived"];
  }, [statusDefs]);

  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const h = (e) => { if (addRef.current && !addRef.current.contains(e.target)) setAddOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const load = () => {
    if (view === "import") return;
    const params = { view };
    if (search) params.q = search;
    if (priorityFilter) params.priority = priorityFilter;
    if (dueFrom) params.due_from = dueFrom;
    if (dueTo) params.due_to = dueTo;
    api.get("/tasks", { params }).then((r) => setTasks(r.data));
    api.get("/tasks/counts").then((r) => setCounts(r.data));
  };
  useEffect(() => { setSelected(new Set()); load(); }, [view, search, priorityFilter, dueFrom, dueTo]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.status === "active")));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/task-statuses").then((r) => setStatusDefs(r.data)).catch(() => {});
  }, []);

  const openNew = (recurring = false) => {
    setForm({ ...EMPTY, assigned_to_id: user?.id || "", owned_by_id: user?.id || "", recurrence: { enabled: recurring, frequency: "weekly" } });
    setPanel({ mode: "new", recurring });
    setAddOpen(false);
  };
  const openEdit = (t) => {
    setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, t[k] ?? EMPTY[k]])) });
    setComment("");
    setPanelTab("details");
    setActivity(null);
    setPanel({ mode: "edit", task: t });
    api.get(`/tasks/${t.id}/activity`).then((r) => setActivity(r.data.events)).catch(() => setActivity([]));
  };

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("open");
    if (!id) return;
    api.get(`/tasks/${id}`).then((r) => openEdit(r.data)).catch(() => toast.error("Task not found"));
    window.history.replaceState({}, "", "/tasks");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remind = async () => {
    try { await api.post(`/tasks/${panel.task.id}/remind`, {}); toast.success("Reminder sent to the notification center"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const named = { ...form,
        client_name: (clients.find((c) => c.id === form.client_id) || {}).first_name ? `${clients.find((c) => c.id === form.client_id).first_name} ${clients.find((c) => c.id === form.client_id).last_name}` : "",
        assigned_to_name: nameOf(form.assigned_to_id), owned_by_name: nameOf(form.owned_by_id),
        followed_by_names: form.followed_by_ids.map(nameOf) };
      if (panel.mode === "edit") await api.put(`/tasks/${panel.task.id}`, named);
      else await api.post("/tasks", named);
      toast.success(panel.mode === "edit" ? "Task updated" : "Task created");
      setPanel(null); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  const nameOf = (id) => { const u = users.find((x) => x.id === id); return u ? `${u.first_name} ${u.last_name}` : ""; };

  const timer = async (t) => {
    const r = await api.post(`/tasks/${t.id}/timer/${t.timer_started_at ? "stop" : "start"}`);
    setTasks((ts) => ts.map((x) => (x.id === t.id ? r.data : x)));
  };

  const bulkAction = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tasks/bulk-action", { ids: [...selected], action: confirm });
      toast.success(`${r.data.modified} task${r.data.modified === 1 ? "" : "s"} ${confirm === "complete" ? "completed" : "archived"}`);
      setConfirm(null); setSelected(new Set()); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/tasks/${panel.task.id}/comments`, { text: comment });
    const t = tasks.find((x) => x.id === panel.task.id);
    setPanel((p) => ({ ...p, task: { ...p.task, comments: [...(p.task.comments || []), { text: comment, by: `${user.first_name} ${user.last_name}`, at: new Date().toISOString() }] } }));
    setComment("");
    toast.success("Comment added");
  };

  const allSelected = tasks.length > 0 && tasks.every((t) => selected.has(t.id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pg = usePagination(tasks);

  return (
    <div>
      <PageHeader title="Tasks" subtitle="Team workload, time tracking & request queue"
        action={
          <div className="flex items-center gap-2">
            <ExportCsvBtn resource="tasks" />
            <Btn variant="outline" data-testid="start-my-day-btn" onClick={() => setFocusOpen(true)}><Zap size={14} /> Start My Day</Btn>
            <InfoTip text="Focus mode walks you through every task assigned to you that is due today or overdue, one at a time. Complete or skip each one until your day is clear." />
            <div className="relative" ref={addRef}>
            <Btn data-testid="add-task-btn" onClick={() => setAddOpen((o) => !o)}><Plus size={16} /> Add New Task <ChevronDown size={14} /></Btn>
            {addOpen && (
              <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-white shadow-[0_12px_40px_-8px_rgba(62,39,35,0.35)] border border-[#EFEBE0] py-2 z-50" data-testid="add-task-menu">
                <button data-testid="create-new-task" onClick={() => openNew(false)} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F5F2EB]"><Plus size={14} /> Create a New Task</button>
                <button data-testid="create-recurring-task" onClick={() => openNew(true)} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F5F2EB]"><Repeat size={14} /> Create Recurring Task</button>
                <button data-testid="create-multiple-task" onClick={() => { setView("import"); setAddOpen(false); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F5F2EB]"><ListPlus size={14} /> Create Multiple Task</button>
              </div>
            )}
            </div>
          </div>
        } />

      <div className="mb-4 flex gap-1 overflow-x-auto rounded-xl bg-[#F5F2EB] p-1" data-testid="task-views">
        {VIEWS.map(([k, l]) => (
          <button key={k} data-testid={`task-view-${k}`} onClick={() => setView(k)}
            className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${view === k ? "bg-[#4E342E] text-white shadow" : "text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
            {l}{k !== "import" && counts[k] !== undefined ? ` (${counts[k]})` : ""}
          </button>
        ))}
      </div>

      {view === "import" ? <ImportTasks users={users} reload={() => api.get("/tasks/counts").then((r) => setCounts(r.data))} /> : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <h2 className="font-heading font-bold text-[#3E2723]">{VIEWS.find(([k]) => k === view)?.[1].replace("My Overdue", "All Overdue Tasks")}</h2>
            <div className="relative ml-auto">
              <Search size={15} className="absolute left-3 top-3 text-[#84675B]" />
              <FInput data-testid="task-search" placeholder="Search here" className="pl-9 h-10 w-60" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="flex items-center gap-1.5 text-sm text-[#6D4C41]"><Filter size={14} />
              <FSelect data-testid="task-priority-filter" className="w-36 h-10" placeholder="Filter by" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                {["low", "medium", "high", "critical"].map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)} priority</option>)}
              </FSelect>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-36"><DateField testId="task-due-from" placeholder="Due from" value={dueFrom} onChange={setDueFrom} /></div>
              <span className="text-xs text-[#84675B]">to</span>
              <div className="w-36"><DateField testId="task-due-to" placeholder="Due to" value={dueTo} onChange={setDueTo} /></div>
              {(dueFrom || dueTo) && <button data-testid="task-date-clear" onClick={() => { setDueFrom(""); setDueTo(""); }} className="text-xs font-semibold text-[#7B6157] hover:text-[#4E342E]">Clear</button>}
            </div>
          </div>

          {selected.size > 0 && (
            <div data-testid="task-bulk-bar" className="mb-3 flex items-center gap-3 rounded-xl bg-[#3E2723] px-4 py-2.5 text-white shadow-lg">
              <span className="text-sm font-semibold">{selected.size} selected</span>
              <span className="h-4 w-px bg-white/20" />
              <button data-testid="task-bulk-complete" onClick={() => setConfirm("complete")} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-white/10"><Check size={14} /> Mark Complete</button>
              <button data-testid="task-bulk-archive" onClick={() => setConfirm("archive")} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-white/10"><Trash2 size={14} /> Archive</button>
              <button aria-label="Clear selection" onClick={() => setSelected(new Set())} className="ml-auto h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center"><X size={14} /></button>
            </div>
          )}

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead><tr>
                  <th className="bg-[#FDFCF8] border-y border-[#EFEBE0] py-3 px-3 w-9">
                    <input type="checkbox" data-testid="task-select-all" className="h-4 w-4 accent-[#4E342E]" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(tasks.map((t) => t.id)))} />
                  </th>
                  {["No", "Title", "Project", "Assigned to", "Due", "Status", "Hours Est", "Hours Act", "", "Timer", ""].map((h, i) => (
                    <th key={i} className="bg-[#FDFCF8] border-y border-[#EFEBE0] text-[#4E342E] font-semibold py-3 px-3 whitespace-nowrap">{h}</th>))}
                </tr></thead>
                <tbody>
                  {pg.pageRows.map((t) => {
                    const secs = liveSeconds(t, nowTs);
                    return (
                      <tr key={t.id} data-testid={`task-row-${t.id}`} className={`border-b border-[#F5F2EB] hover:bg-[#FDFCF8] ${selected.has(t.id) ? "bg-[#F5F2EB]" : ""}`}>
                        <td className="py-2.5 px-3"><input type="checkbox" data-testid={`task-select-${t.id}`} className="h-4 w-4 accent-[#4E342E]" checked={selected.has(t.id)} onChange={() => toggle(t.id)} /></td>
                        <td className="py-2.5 px-3 tabular-nums text-[#6D4C41]">{t.number}</td>
                        <td className="py-2.5 px-3 font-semibold text-[#3E2723] max-w-[220px]">
                          <span className="flex items-center gap-1.5">{t.recurrence?.enabled && <Repeat size={12} className="text-[#7B6157] shrink-0" />}
                            <Link to={`/tasks/${t.id}`} data-testid={`task-title-link-${t.id}`} className="truncate hover:underline">{t.title}</Link></span>
                          {t.client_name && (t.client_id
                            ? <Link to={`/clients/${t.client_id}/console`} data-testid={`task-client-link-${t.id}`} className="block text-[11px] font-normal text-[#7B6157] hover:underline">{t.client_name}</Link>
                            : <span className="block text-[11px] font-normal text-[#7B6157]">{t.client_name}</span>)}
                        </td>
                        <td className="py-2.5 px-3 text-[#4E342E] max-w-[180px] truncate">{t.project || "-"}</td>
                        <td className="py-2.5 px-3 whitespace-nowrap">{t.assigned_to_name
                          ? (user?.role === "admin" || t.assigned_to_id === user?.id
                            ? <Link to={`/staff/${t.assigned_to_id}`} data-testid={`task-assignee-link-${t.id}`} className="hover:underline">{t.assigned_to_name}</Link>
                            : t.assigned_to_name)
                          : <span className="text-[#8D5A00] text-xs font-semibold">Unassigned</span>}</td>
                        <td className="py-2.5 px-3 whitespace-nowrap"><DueCell t={t} doneKeys={doneKeys} /></td>
                        <td className="py-2.5 px-3 whitespace-nowrap"><StatusChip status={t.status} meta={statusMeta} /></td>
                        <td className="py-2.5 px-3 tabular-nums">{t.estimated_hours ? `${t.estimated_hours} hours` : "-"}</td>
                        <td className="py-2.5 px-3 tabular-nums">{secs ? `${(secs / 3600).toFixed(1)} hours` : "-"}</td>
                        <td className="py-2.5 px-3"><span title={`${t.priority} priority`} className={`inline-block h-4 w-4 rounded ${PRIORITY_COLORS[t.priority] || "bg-[#BDBDBD]"}`} data-testid={`task-priority-dot-${t.id}`} /></td>
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          <span className="inline-flex items-center gap-2">
                            <span className={`tabular-nums text-xs ${t.timer_started_at ? "text-[#2E7D32] font-bold" : "text-[#6D4C41]"}`} data-testid={`task-timer-${t.id}`}>{fmtHMS(secs)}</span>
                            <button data-testid={`task-timer-btn-${t.id}`} aria-label={t.timer_started_at ? "Stop timer" : "Start timer"} onClick={() => timer(t)}
                              className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${t.timer_started_at ? "bg-[#2E7D32] text-white" : "bg-[#F5F2EB] text-[#4E342E] hover:bg-[#EDE7DC]"}`}>
                              {t.timer_started_at ? <Square size={11} /> : <Play size={12} />}
                            </button>
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          <button data-testid={`task-view-btn-${t.id}`} aria-label="View task" onClick={() => openEdit(t)}
                            className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#4E342E] flex items-center justify-center hover:bg-[#EDE7DC]"><Eye size={13} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationBar {...pg} testId="tasks-pagination" />
            {tasks.length === 0 && <Empty text="No tasks in this view" />}
          </Card>
        </>
      )}

      <SlidePanel open={!!panel} onClose={() => setPanel(null)}
        title={panel?.mode === "edit" ? `Task #${panel.task.number}` : panel?.recurring ? "Create Recurring Task" : "Create a New Task"}
        subtitle={panel?.mode === "edit" ? panel.task.title : "Assign work with time tracking, followers & recurrence"} width="max-w-2xl" testId="task-slide-panel">
        {panel && (
          <>
            {panel.mode === "edit" && (
              <div className="mb-4 flex items-center gap-2">
                {[["details", "Details"], ["activity", "Activity & Audit"]].map(([k, l]) => (
                  <button key={k} data-testid={`task-panel-tab-${k}`} onClick={() => setPanelTab(k)}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold ${panelTab === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41]"}`}>{l}</button>
                ))}
                <Btn variant="outline" data-testid="task-remind-btn" className="ml-auto px-3 py-1.5 text-xs" onClick={remind}><BellRing size={13} /> Set Reminder</Btn>
              </div>
            )}
            {panel.mode === "edit" && panelTab === "activity" ? (
              <div className="pb-16" data-testid="task-activity-tab">
                <h4 className="font-heading text-sm font-bold text-[#3E2723] mb-3 flex items-center gap-2"><History size={14} /> Full activity & audit trail</h4>
                {activity === null ? <div className="py-8 text-center text-sm text-[#84675B]">Loading activity…</div> :
                  activity.length === 0 ? <div className="py-8 text-center text-sm text-[#84675B]">No recorded activity yet.</div> : (
                    <div className="relative space-y-3 before:absolute before:left-[7px] before:top-1 before:bottom-1 before:w-px before:bg-[#E5DFD3]">
                      {activity.map((e, i) => (
                        <div key={i} className="relative pl-6" data-testid={`task-activity-event-${i}`}>
                          <span className={`absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white ${e.action === "create" ? "bg-[#2E7D32]" : e.action === "comment" ? "bg-[#1565C0]" : e.action === "sign" ? "bg-[#5E35B1]" : "bg-[#8D6E63]"}`} />
                          <div className="text-sm"><span className="font-semibold capitalize text-[#3E2723]">{e.action}</span>
                            <span className="text-[#5D4037]"> by {e.by}</span></div>
                          {e.detail && <div className="text-xs text-[#5D4037]">{e.detail}</div>}
                          <div className="text-[11px] text-[#84675B]">{dayjs(e.at).format("MMM D, YYYY h:mm A")}</div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            ) : (
              <TaskForm form={form} setForm={setForm} users={users} clients={clients} onSave={save} onCancel={() => setPanel(null)} busy={busy} isEdit={panel.mode === "edit"} statusDefs={statusDefs} />
            )}
            {panel.mode === "edit" && panelTab === "details" && (
              <div className="mt-6 border-t border-[#F1EEE7] pt-4 pb-16">
                <h4 className="font-heading text-sm font-bold text-[#3E2723] mb-3 flex items-center gap-2"><MessageSquare size={14} /> Comments ({(panel.task.comments || []).length})</h4>
                <div className="space-y-2 mb-3">
                  {(panel.task.comments || []).map((c, i) => (
                    <div key={i} className="rounded-xl bg-[#F5F2EB] px-4 py-2.5 text-sm">
                      <span className="font-semibold text-[#3E2723]">{c.by}</span>
                      <span className="text-xs text-[#84675B] ml-2">{dayjs(c.at).format("MM/DD HH:mm")}</span>
                      <p className="text-[#4E342E] mt-0.5">{c.text}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <FInput data-testid="task-comment-input" placeholder="Add a comment…" value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} />
                  <Btn data-testid="task-comment-send" type="button" className="rounded-lg shrink-0" onClick={addComment}><Send size={14} /></Btn>
                </div>
              </div>
            )}
          </>
        )}
      </SlidePanel>

      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={bulkAction} busy={busy}
        title={confirm === "complete" ? `Complete ${selected.size} task${selected.size === 1 ? "" : "s"}` : `Archive ${selected.size} task${selected.size === 1 ? "" : "s"}`}
        confirmWord={confirm === "complete" ? "COMPLETE" : "ARCHIVE"} confirmLabel={confirm === "complete" ? "Complete" : "Archive"}
        warning={confirm === "complete" ? "Selected tasks will be marked completed with today's date." : "Archived tasks disappear from all views but stay in the record."} />

      <TaskFocusMode open={focusOpen} onClose={() => { setFocusOpen(false); load(); }} doneKeys={doneKeys} onCompleted={() => api.get("/tasks/counts").then((r) => setCounts(r.data)).catch(() => {})} />
    </div>
  );
}
